import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { CustomerPicker } from '../components/Customers.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { dateTime, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi, Table } from './Reports.tsx';

/**
 * Helpdesk: customers' questions and problems as tickets. The list on the left, the conversation
 * on the right; answers go to the customer, notes stay inside the team. Teams share new tickets out
 * by themselves, and service levels say how soon an answer is due.
 */

type Tab = 'tickets' | 'teams' | 'slas' | 'macros' | 'settings';
type State = 'new' | 'open' | 'waiting' | 'solved' | 'closed' | 'cancelled';
type Kind = 'question' | 'problem' | 'request' | 'complaint' | 'other';
type Channel = 'web' | 'email' | 'phone' | 'chat' | 'visit' | 'staff';
type SlaState = 'met' | 'late' | 'due' | null;
const FILTERS = ['open', 'mine', 'unassigned', 'late', 'waiting', 'solved', 'closed', 'all'] as const;
type Filter = (typeof FILTERS)[number];
const KINDS: Kind[] = ['question', 'problem', 'request', 'complaint', 'other'];
const CHANNELS: Channel[] = ['phone', 'email', 'chat', 'visit', 'web', 'staff'];
const COLORS = ['blue', 'green', 'orange', 'purple', 'red', 'teal', 'yellow', 'grey'] as const;
const ASSIGNMENT = ['balanced', 'rotation', 'manual'] as const;

interface Row {
  id: string;
  number: string;
  subject: string;
  state: State;
  priority: number;
  kind: Kind;
  channel: Channel;
  team: string | null;
  team_name: string | null;
  team_color: string | null;
  customer: string | null;
  customer_name: string | null;
  contact_email: string | null;
  assignee: string | null;
  assignee_name: string | null;
  created_at: string;
  updated_at: string;
  first_due: string | null;
  resolve_due: string | null;
  sla_first: SlaState;
  sla_resolve: SlaState;
  late: boolean;
  needs_answer: boolean;
  rating: number | null;
  messages: number;
}
interface Full extends Row {
  contact_name: string | null;
  contact_phone: string | null;
  first_response_at: string | null;
  solved_at: string | null;
  rating_comment: string | null;
  sla_name: string | null;
  link: string | null;
  last_mail: { state: 'queued' | 'sent' | 'failed' | 'skipped'; error: string | null; at: string } | null;
  conversation: { id: string; kind: 'customer' | 'reply' | 'note' | 'event'; body: string; author_name: string | null; at: string }[];
  others: Row[];
}
interface Overview {
  month: { open: number; unassigned: number; mine: number; late: number; new_30: number; solved_30: number; first_hours: number; sla_count: number; sla_met: number; rating: number; ratings: number };
  teams: { id: string; name: string; color: string; open: number; unassigned: number }[];
  portal: boolean;
  portal_path: string;
}
interface Setup {
  teams: { id: string; name: string; color: string }[];
  people: { id: string; name: string }[];
  macros: { id: string; name: string; body: string; team: string | null }[];
}
interface Team {
  id: string;
  name: string;
  description: string | null;
  color: (typeof COLORS)[number];
  assignment: (typeof ASSIGNMENT)[number];
  members: string[];
  member_names: string[];
  public: boolean;
  active: boolean;
  tickets: number;
}
interface Sla {
  id: string;
  name: string;
  team: string | null;
  priority: number;
  first_hours: number;
  resolve_hours: number;
  active: boolean;
}
interface Macro {
  id: string;
  name: string;
  body: string;
  team: string | null;
  active: boolean;
}
interface Settings {
  portal: boolean;
  portal_title: string | null;
  portal_intro: string | null;
  notify: boolean;
  site_url: string | null;
  rating: boolean;
  reopen_days: number;
  close_after_days: number;
  mail_ready: boolean;
  portal_path: string;
  portal_url: string | null;
}

const TABS: { key: Tab; icon: IconName; perm?: string }[] = [
  { key: 'tickets', icon: 'lifebuoy' },
  { key: 'teams', icon: 'users' },
  { key: 'slas', icon: 'clock' },
  { key: 'macros', icon: 'note' },
  { key: 'settings', icon: 'settings', perm: 'helpdesk.manage' },
];
const STATE: Record<State, { tone: Tone; icon: IconName }> = {
  new: { tone: 'info', icon: 'sparkle' },
  open: { tone: 'primary', icon: 'lifebuoy' },
  waiting: { tone: 'warn', icon: 'clock' },
  solved: { tone: 'ok', icon: 'checkCircle' },
  closed: { tone: 'neutral', icon: 'check' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const PRIORITY_TONE = ['low', 'normal', 'high', 'urgent'] as const;

function StateBadge({ state }: { state: State }) {
  const s = STATE[state];
  return <StatusBadge tone={s.tone} icon={s.icon} label={t(`hd.st_${state}` as MessageKey)} />;
}
function Priority({ p, label = false }: { p: number; label?: boolean }) {
  const k = PRIORITY_TONE[p] ?? 'normal';
  return (
    <span className={`hd-prio ${k}`} title={t(`hd.p_${k}` as MessageKey)}>
      <span className="hd-prio-bars" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      {label ? <span>{t(`hd.p_${k}` as MessageKey)}</span> : <span className="sr-only">{t(`hd.p_${k}` as MessageKey)}</span>}
    </span>
  );
}
/** "Due in 3 h", "2 h late", "Met". */
function Due({ due, state, at }: { due: string | null; state: SlaState; at?: string | null }) {
  if (!due || !state) return <span className="muted">—</span>;
  if (state === 'met' || (state === 'late' && at))
    return (
      <span className="hd-due-done">
        <span className={`hd-due ${state === 'met' ? 'ok' : 'late'}`}>{state === 'met' ? t('hd.sla_met') : t('hd.late')}</span>
        {at ? <span className="muted small">{dateTime(at)}</span> : null}
      </span>
    );
  const mins = Math.round((new Date(due).getTime() - Date.now()) / 60_000);
  const span = (m: number) =>
    Math.abs(m) >= 1440
      ? t('hd.days_n', { n: Math.round(Math.abs(m) / 1440) })
      : Math.abs(m) >= 60
        ? t('hd.hours_n', { n: Math.round(Math.abs(m) / 60) })
        : t('hd.minutes_n', { n: Math.max(1, Math.abs(m)) });
  if (state === 'late') return <span className="hd-due late">{t('hd.late_by', { time: span(mins) })}</span>;
  return <span className={`hd-due${mins < 120 ? ' soon' : ''}`}>{t('hd.due_in', { time: span(mins) })}</span>;
}

export function Helpdesk() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const tabs = TABS.filter((x) => !x.perm || can(x.perm));
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'tickets') as Tab;
  const { data: o, reload } = useAction<Overview>('helpdesk.overview');
  const { data: setup, reload: reloadSetup } = useAction<Setup>('helpdesk.setup');
  const [version, setVersion] = useState(0);
  const [creating, setCreating] = useState(false);
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await Promise.all([reload(), reloadSetup()]);
  };
  useEffect(
    () =>
      subscribe(['helpdesk'], () => {
        setVersion((v) => v + 1);
        void reload();
      }),
    [reload],
  );
  const answer = can('helpdesk.answer');
  const slaPct = o && o.month.sla_count ? Math.round((o.month.sla_met / o.month.sla_count) * 100) : null;

  return (
    <Shell>
      <div className="page reports hd-page">
        <div className="page-head">
          <div>
            <h1>{t('hd.title')}</h1>
            <p className="muted">{t('hd.subtitle')}</p>
          </div>
          <div className="page-actions">
            {o?.portal ? (
              <a className="btn btn-secondary btn-md" href={o.portal_path} target="_blank" rel="noreferrer">
                <Icon name="globe" size={18} />
                <span>{t('hd.support_page')}</span>
              </a>
            ) : null}
            {answer ? (
              <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                {t('hd.new_ticket')}
              </Button>
            ) : null}
          </div>
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('hd.at_a_glance')}>
            <Kpi label={t('hd.k_open')} value={String(o.month.open)} hint={o.month.unassigned ? tn('hd.k_unassigned', o.month.unassigned) : t('hd.k_all_taken')} warn={o.month.unassigned > 0} />
            <Kpi label={t('hd.k_late')} value={String(o.month.late)} hint={o.month.late ? t('hd.k_late_hint') : t('hd.k_on_time')} warn={o.month.late > 0} />
            <Kpi
              label={t('hd.k_first')}
              value={o.month.sla_count || o.month.first_hours ? t('hd.hours_short', { n: o.month.first_hours }) : '—'}
              hint={slaPct === null ? t('hd.k_first_hint') : t('hd.k_sla_pct', { n: slaPct })}
              warn={slaPct !== null && slaPct < 80}
            />
            <Kpi label={t('hd.k_rating')} value={o.month.ratings ? `${o.month.rating.toFixed(1)} / 5` : '—'} hint={o.month.ratings ? tn('hd.k_ratings', o.month.ratings) : t('hd.k_no_ratings')} />
          </section>
        )}
        <div className="segmented tabs hd-tabs" role="tablist" aria-label={t('hd.sections')}>
          {tabs.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'tickets' ? null : x.key, t: null, f: null })}>
              <Icon name={x.icon} size={16} />
              {t(`hd.tab_${x.key}` as MessageKey)}
              {x.key === 'tickets' && o?.month.open ? <span className="count">{o.month.open}</span> : null}
            </button>
          ))}
        </div>
        {!o || !setup ? (
          <Skeleton h={400} />
        ) : tab === 'tickets' ? (
          <TicketsTab version={version} o={o} setup={setup} params={params} set={set} onChange={changed} />
        ) : tab === 'teams' ? (
          <TeamsTab key={version} onChange={changed} />
        ) : tab === 'slas' ? (
          <SlasTab key={version} teams={setup.teams} />
        ) : tab === 'macros' ? (
          <MacrosTab key={version} teams={setup.teams} onChange={changed} />
        ) : (
          <SettingsTab onChange={changed} />
        )}
      </div>
      {creating && setup ? (
        <NewTicket
          setup={setup}
          onClose={() => setCreating(false)}
          onSaved={async (id) => {
            setCreating(false);
            await changed();
            set({ tab: null, t: id });
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- tickets: list and conversation ----------------------------------------------------------------

function TicketsTab({
  version,
  o,
  setup,
  params,
  set,
  onChange,
}: {
  version: number;
  o: Overview;
  setup: Setup;
  params: URLSearchParams;
  set: (n: Record<string, string | null>) => void;
  onChange: () => Promise<void>;
}) {
  const filter = (FILTERS as readonly string[]).includes(params.get('f') ?? '') ? (params.get('f') as Filter) : 'open';
  const team = params.get('team');
  const open = params.get('t');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  const { data, reload } = useAction<Row[]>('helpdesk.tickets', { filter: query ? 'all' : filter, team, q: query, v: version });
  useEffect(() => {
    void reload();
  }, [version, reload]);
  const counts: Partial<Record<Filter, number>> = { open: o.month.open, mine: o.month.mine, unassigned: o.month.unassigned, late: o.month.late };
  return (
    <div className={`hd-split${open ? ' has-open' : ''}`}>
      <section className="hd-list" aria-label={t('hd.tickets')}>
        <div className="hd-list-tools">
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" value={q} placeholder={t('hd.search')} aria-label={t('hd.search')} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div className="hd-filters" role="group" aria-label={t('hd.show')}>
            {FILTERS.map((f) => (
              <button key={f} className={`hd-chip${filter === f && !query ? ' on' : ''}`} aria-pressed={filter === f && !query} onClick={() => set({ f: f === 'open' ? null : f })}>
                {t(`hd.f_${f}` as MessageKey)}
                {counts[f] ? <span className={`count${f === 'late' ? ' warn' : ''}`}>{counts[f]}</span> : null}
              </button>
            ))}
          </div>
          {o.teams.length > 1 ? (
            <select className="input hd-team-select" value={team ?? ''} aria-label={t('hd.team')} onChange={(e) => set({ team: e.target.value || null })}>
              <option value="">{t('hd.all_teams')}</option>
              {o.teams.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name} ({x.open})
                </option>
              ))}
            </select>
          ) : null}
        </div>
        {!data ? (
          <div className="hd-rows">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} h={72} />
            ))}
          </div>
        ) : !data.length ? (
          <EmptyState icon="lifebuoy" title={query ? t('hd.nothing_found') : t(`hd.empty_${filter}` as MessageKey)} body={filter === 'open' && !query ? t('hd.empty_open_hint') : undefined} />
        ) : (
          <ul className="hd-rows">
            {data.map((r) => (
              <li key={r.id}>
                <button className={`hd-row${open === r.id ? ' on' : ''}${r.late ? ' late' : ''}`} aria-current={open === r.id ? 'true' : undefined} onClick={() => set({ t: r.id })}>
                  <span className="hd-row-top">
                    <Priority p={r.priority} />
                    <span className="hd-num">{r.number}</span>
                    {r.team_name ? <span className={`hd-team-tag tone-${r.team_color ?? 'grey'}`}>{r.team_name}</span> : null}
                    <span className="hd-row-when muted small" title={dateTime(r.updated_at)}>
                      {since(r.updated_at)}
                    </span>
                  </span>
                  <span className="hd-row-subject">
                    {r.needs_answer ? <span className="hd-unread" aria-label={t('hd.needs_answer')} /> : null}
                    <strong>{r.subject}</strong>
                  </span>
                  <span className="hd-row-foot muted small">
                    <span className="hd-row-who">{r.customer_name ?? t('hd.no_contact')}</span>
                    <span>·</span>
                    <span>{r.assignee_name ?? t('hd.unassigned')}</span>
                    {r.late ? (
                      <span className="hd-late-tag">
                        <Icon name="alert" size={12} /> {t('hd.late')}
                      </span>
                    ) : (
                      <StateBadge state={r.state} />
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="hd-detail" aria-label={t('hd.ticket')}>
        {open ? (
          <TicketView key={open} id={open} version={version} setup={setup} onBack={() => set({ t: null })} onOpen={(id) => set({ t: id })} onChange={onChange} />
        ) : (
          <div className="hd-empty-detail">
            <EmptyState icon="messages" title={t('hd.pick_ticket')} body={t('hd.pick_ticket_hint')} />
          </div>
        )}
      </section>
    </div>
  );
}

function TicketView({ id, version, setup, onBack, onOpen, onChange }: { id: string; version: number; setup: Setup; onBack: () => void; onOpen: (id: string) => void; onChange: () => Promise<void> }) {
  const { can, me } = useSession();
  const { toast, fail } = useUi();
  const { busy, run } = useSave();
  const { data: x, reload } = useAction<Full>('helpdesk.ticket', { id });
  const [mode, setMode] = useState<'reply' | 'note'>('reply');
  const [text, setText] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [linking, setLinking] = useState(false);
  useEffect(() => {
    void reload();
  }, [version, reload]);
  if (!x) return <Skeleton h={500} />;
  const answer = can('helpdesk.answer');
  const finished = x.state === 'closed' || x.state === 'cancelled';
  const after = async () => {
    await reload();
    await onChange();
  };
  const send = (then: 'keep' | 'waiting' | 'solved') => {
    if (!text.trim()) return;
    void run(
      async () => {
        await action('helpdesk.reply', { id, body: text.trim(), note: mode === 'note', then });
        setText('');
        await after();
      },
      mode === 'note' ? t('hd.note_saved') : then === 'solved' ? t('hd.sent_solved') : t('hd.sent'),
    );
  };
  const update = (data: Record<string, unknown>, done?: string) => void run(async () => (await action('helpdesk.update', { id, ...data }), await after()), done);
  const state = (s: State, reason?: string) => run(async () => (await action('helpdesk.set_state', { id, state: s, reason: reason ?? null }), await after()), t(`hd.to_${s}` as MessageKey));
  const macros = setup.macros.filter((m) => !m.team || m.team === x.team);
  const first = (x.contact_name ?? x.customer_name ?? '').split(' ')[0] ?? '';
  const useMacro = (body: string) => {
    setMode('reply');
    setText((cur) => (cur.trim() ? `${cur.trimEnd()}\n\n${body}` : first ? `${t('hd.hello', { name: first })}\n\n${body}` : body));
  };
  const copy = (s: string) => void navigator.clipboard?.writeText(s).then(() => toast(t('hd.copied')), fail);

  return (
    <article className="hd-ticket">
      <header className="hd-ticket-head">
        <IconButton icon="back" label={t('hd.back')} className="hd-back" onClick={onBack} />
        <div className="hd-ticket-title">
          <p className="muted small">
            <span className="hd-num">{x.number}</span> · {t(`hd.ch_${x.channel}` as MessageKey)} · {dateTime(x.created_at)}
          </p>
          <h2>{x.subject}</h2>
        </div>
        <StateBadge state={x.state} />
      </header>
      {answer ? (
        <div className="hd-actions">
          {!x.assignee || x.assignee !== me?.userId ? (
            <Button size="sm" icon="contact" busy={busy} disabled={finished} onClick={() => update({ assignee: me?.userId }, t('hd.taken'))}>
              {t('hd.take')}
            </Button>
          ) : null}
          {x.state === 'solved' || finished ? (
            <Button size="sm" icon="undo" busy={busy} onClick={() => void state('open')}>
              {t('hd.reopen')}
            </Button>
          ) : (
            <Button size="sm" icon="checkCircle" busy={busy} onClick={() => void state('solved')}>
              {t('hd.mark_solved')}
            </Button>
          )}
          {x.state === 'solved' ? (
            <Button size="sm" icon="check" busy={busy} onClick={() => void state('closed')}>
              {t('hd.close')}
            </Button>
          ) : null}
          {!finished ? (
            <Button size="sm" variant="ghost" icon="x" onClick={() => setCancelling(true)}>
              {t('hd.cancel_ticket')}
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className="hd-ticket-body">
        <div className="hd-conv">
          <ol className="hd-thread" aria-label={t('hd.conversation')}>
            {x.conversation.map((m) =>
              m.kind === 'event' ? (
                <li key={m.id} className="hd-event muted small">
                  <span>{m.body}</span> · <span title={dateTime(m.at)}>{since(m.at)}</span>
                  {m.author_name ? <span> · {m.author_name}</span> : null}
                </li>
              ) : (
                <li key={m.id} className={`hd-msg ${m.kind}`}>
                  <span className="hd-msg-who small">
                    <strong>
                      {m.kind === 'customer'
                        ? m.author_name
                          ? t('hd.written_by', { name: m.author_name })
                          : (x.contact_name ?? x.customer_name ?? t('hd.customer'))
                        : (m.author_name ?? t('hd.team'))}
                    </strong>
                    {m.kind === 'note' ? (
                      <span className="hd-note-tag">
                        <Icon name="lock" size={12} /> {t('hd.internal_note')}
                      </span>
                    ) : null}
                    <span className="muted" title={dateTime(m.at)}>
                      {dateTime(m.at)}
                    </span>
                  </span>
                  <div className="hd-bubble">{m.body}</div>
                </li>
              ),
            )}
          </ol>
          {x.rating ? (
            <p className="hd-rating">
              <span className="hd-stars" aria-label={t('hd.rated_n', { n: x.rating })}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Icon key={n} name="star" size={16} className={n <= x.rating! ? 'on' : ''} />
                ))}
              </span>
              {x.rating_comment ? <span>“{x.rating_comment}”</span> : <span className="muted">{t('hd.rated_n', { n: x.rating })}</span>}
            </p>
          ) : null}
          {answer && x.state !== 'cancelled' && (x.state !== 'closed' || mode === 'note') ? (
            <form
              className={`hd-composer ${mode}`}
              onSubmit={(e) => {
                e.preventDefault();
                send('keep');
              }}
            >
              <div className="hd-composer-top">
                <div className="segmented subtle" role="radiogroup" aria-label={t('hd.write')}>
                  <button type="button" role="radio" aria-checked={mode === 'reply'} className={mode === 'reply' ? 'active' : ''} onClick={() => setMode('reply')}>
                    <Icon name="send" size={14} /> {t('hd.reply')}
                  </button>
                  <button type="button" role="radio" aria-checked={mode === 'note'} className={mode === 'note' ? 'active' : ''} onClick={() => setMode('note')}>
                    <Icon name="lock" size={14} /> {t('hd.note')}
                  </button>
                </div>
                {macros.length && mode === 'reply' ? (
                  <select className="input hd-macro" value="" aria-label={t('hd.use_macro')} onChange={(e) => e.target.value && useMacro(macros.find((m) => m.id === e.target.value)!.body)}>
                    <option value="">{t('hd.use_macro')}</option>
                    {macros.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
              <textarea
                className="input"
                rows={4}
                value={text}
                maxLength={10000}
                aria-label={mode === 'note' ? t('hd.note') : t('hd.reply')}
                placeholder={mode === 'note' ? t('hd.note_hint') : x.contact_email ? t('hd.reply_hint', { email: x.contact_email }) : t('hd.reply_hint_link')}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send('keep');
                }}
              />
              <div className="hd-composer-foot">
                <span className="muted small">{mode === 'note' ? t('hd.note_private') : t('hd.ctrl_enter')}</span>
                {mode === 'note' ? (
                  <Button type="submit" icon="lock" busy={busy} disabled={!text.trim()}>
                    {t('hd.save_note')}
                  </Button>
                ) : (
                  <span className="hd-send">
                    <Button icon="clock" busy={busy} disabled={!text.trim()} onClick={() => send('waiting')}>
                      {t('hd.send_wait')}
                    </Button>
                    <Button icon="checkCircle" busy={busy} disabled={!text.trim()} onClick={() => send('solved')}>
                      {t('hd.send_solve')}
                    </Button>
                    <Button type="submit" variant="primary" icon="send" busy={busy} disabled={!text.trim()}>
                      {t('hd.send')}
                    </Button>
                  </span>
                )}
              </div>
            </form>
          ) : null}
        </div>
        <aside className="hd-props" aria-label={t('hd.details')}>
          <div className="hd-prop-group">
            <h3 className="small muted">{t('hd.customer')}</h3>
            <p className="hd-contact">
              <strong>{x.customer_name ?? x.contact_name ?? t('hd.no_contact')}</strong>
              {x.contact_email ? (
                <a href={`mailto:${x.contact_email}`} className="small">
                  {x.contact_email}
                </a>
              ) : null}
              {x.contact_phone ? (
                <a href={`tel:${x.contact_phone}`} className="small tabular">
                  {x.contact_phone}
                </a>
              ) : null}
            </p>
            {answer ? (
              <button className="link small hd-link-btn" onClick={() => setLinking(true)}>
                <Icon name="contact" size={14} /> {x.customer ? t('hd.change_customer') : t('hd.link_customer')}
              </button>
            ) : null}
          </div>
          <Field label={t('hd.team')}>
            <select className="input" value={x.team ?? ''} disabled={!answer || finished} onChange={(e) => update({ team: e.target.value || null })}>
              <option value="">{t('hd.no_team')}</option>
              {setup.teams.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('hd.assignee')}>
            <select className="input" value={x.assignee ?? ''} disabled={!answer || finished} onChange={(e) => update({ assignee: e.target.value || null })}>
              <option value="">{t('hd.unassigned')}</option>
              {setup.people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('hd.priority')}>
            <select className="input" value={x.priority} disabled={!answer || finished} onChange={(e) => update({ priority: Number(e.target.value) })}>
              {[0, 1, 2, 3].map((p) => (
                <option key={p} value={p}>
                  {t(`hd.p_${PRIORITY_TONE[p]}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('hd.kind')}>
            <select className="input" value={x.kind} disabled={!answer || finished} onChange={(e) => update({ kind: e.target.value })}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`hd.kind_${k}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
          <div className="hd-prop-group">
            <h3 className="small muted">{x.sla_name ? t('hd.sla_named', { name: x.sla_name }) : t('hd.sla')}</h3>
            {x.sla_name ? (
              <dl className="lc-facts">
                <dt>{t('hd.first_answer')}</dt>
                <dd>
                  <Due due={x.first_due} state={x.sla_first} at={x.first_response_at} />
                </dd>
                <dt>{t('hd.solution')}</dt>
                <dd>
                  <Due due={x.resolve_due} state={x.sla_resolve} at={x.solved_at} />
                </dd>
              </dl>
            ) : (
              <p className="muted small">{t('hd.no_sla')}</p>
            )}
          </div>
          <div className="hd-prop-group">
            <h3 className="small muted">{t('hd.customer_link')}</h3>
            {x.link ? (
              <div className="lc-link">
                <code>{x.link}</code>
                <IconButton icon="copy" label={t('hd.copy_link')} onClick={() => copy(x.link!)} />
              </div>
            ) : (
              <p className="muted small">{t('hd.no_link')}</p>
            )}
            {x.last_mail ? (
              <p className={`small hd-mail ${x.last_mail.state}`}>
                <Icon name={x.last_mail.state === 'sent' ? 'checkCircle' : x.last_mail.state === 'queued' ? 'clock' : 'alert'} size={14} />{' '}
                {t(`hd.mail_${x.last_mail.state}` as MessageKey, { at: since(x.last_mail.at) })}
                {x.last_mail.error ? <span className="muted"> — {x.last_mail.error}</span> : null}
              </p>
            ) : null}
          </div>
          {x.others.length ? (
            <div className="hd-prop-group">
              <h3 className="small muted">{tn('hd.other_tickets', x.others.length)}</h3>
              <ul className="hd-others">
                {x.others.map((r) => (
                  <li key={r.id}>
                    <button className="link hd-other" onClick={() => onOpen(r.id)}>
                      <span className="hd-num">{r.number}</span> {r.subject}
                    </button>
                    <StateBadge state={r.state} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </aside>
      </div>
      {cancelling ? (
        <CancelDialog
          onClose={() => setCancelling(false)}
          onDone={async (reason) => {
            if (await state('cancelled', reason)) setCancelling(false);
          }}
          busy={busy}
        />
      ) : null}
      {linking ? (
        <CustomerPicker
          title={t('hd.link_customer')}
          onClose={() => setLinking(false)}
          onPick={async (c) => {
            setLinking(false);
            update({ customer: c.id }, t('hd.customer_linked'));
          }}
        />
      ) : null}
    </article>
  );
}

function CancelDialog({ onClose, onDone, busy }: { onClose: () => void; onDone: (reason: string) => Promise<void>; busy: boolean }) {
  const [reason, setReason] = useState('');
  return (
    <Dialog
      title={t('hd.cancel_title')}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('hd.keep')}</Button>
          <Button variant="danger" icon="x" busy={busy} disabled={!reason.trim()} onClick={() => void onDone(reason.trim())}>
            {t('hd.cancel_ticket')}
          </Button>
        </>
      }
    >
      <Field label={t('hd.cancel_reason')} hint={t('hd.cancel_hint')} required>
        <textarea className="input" rows={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Dialog>
  );
}

// ---- a new ticket ------------------------------------------------------------------------------

function NewTicket({ setup, onClose, onSaved }: { setup: Setup; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [picking, setPicking] = useState(false);
  const [f, setF] = useState({
    subject: '',
    body: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    team: setup.teams[0]?.id ?? '',
    channel: 'phone' as Channel,
    kind: 'question' as Kind,
    priority: 1,
    assign_me: true,
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF({ ...f, [k]: v });
  const errors = {
    subject: f.subject.trim().length < 3 ? t('hd.e_subject') : null,
    body: !f.body.trim() ? t('hd.e_body') : null,
    contact: !customer && !f.contact_name.trim() && !f.contact_email.trim() && !f.contact_phone.trim() ? t('hd.e_contact') : null,
    email: f.contact_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contact_email.trim()) ? t('hd.e_email') : null,
  };
  const err = (e: string | null) => (tried ? e : null);
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      const r = await action<{ id: string; number: string }>('helpdesk.create', {
        subject: f.subject.trim(),
        body: f.body.trim(),
        customer: customer?.id ?? null,
        contact_name: f.contact_name.trim() || null,
        contact_email: f.contact_email.trim() || null,
        contact_phone: f.contact_phone.trim() || null,
        team: f.team || null,
        channel: f.channel,
        kind: f.kind,
        priority: f.priority,
        assign_me: f.assign_me,
      });
      await onSaved(r.id);
    }, t('hd.created'));
  };
  return (
    <Dialog
      title={t('hd.new_ticket')}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('hd.cancel')}</Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('hd.create')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('hd.customer')} error={err(errors.contact)}>
          {customer ? (
            <div className="hd-picked">
              <Icon name="contact" size={18} />
              <strong>{customer.name}</strong>
              <IconButton icon="x" label={t('hd.remove_customer')} onClick={() => setCustomer(null)} />
            </div>
          ) : (
            <Button icon="search" onClick={() => setPicking(true)}>
              {t('hd.pick_customer')}
            </Button>
          )}
        </Field>
        {!customer ? (
          <div className="form-grid three">
            <Field label={t('hd.contact_name')}>
              <input className="input" value={f.contact_name} maxLength={80} onChange={(e) => set('contact_name', e.target.value)} />
            </Field>
            <Field label={t('hd.contact_email')} error={err(errors.email)}>
              <input className="input" type="email" value={f.contact_email} maxLength={160} onChange={(e) => set('contact_email', e.target.value)} />
            </Field>
            <Field label={t('hd.contact_phone')}>
              <input className="input tabular" type="tel" value={f.contact_phone} maxLength={40} onChange={(e) => set('contact_phone', e.target.value)} />
            </Field>
          </div>
        ) : null}
        <Field label={t('hd.subject')} required error={err(errors.subject)}>
          <input className="input" value={f.subject} maxLength={160} placeholder={t('hd.subject_hint')} onChange={(e) => set('subject', e.target.value)} />
        </Field>
        <Field label={t('hd.what_asked')} required error={err(errors.body)}>
          <textarea className="input" rows={4} value={f.body} maxLength={10000} onChange={(e) => set('body', e.target.value)} />
        </Field>
        <div className="form-grid three">
          <Field label={t('hd.channel')}>
            <select className="input" value={f.channel} onChange={(e) => set('channel', e.target.value as Channel)}>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {t(`hd.ch_${c}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('hd.team')}>
            <select className="input" value={f.team} onChange={(e) => set('team', e.target.value)}>
              <option value="">{t('hd.no_team')}</option>
              {setup.teams.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('hd.kind')}>
            <select className="input" value={f.kind} onChange={(e) => set('kind', e.target.value as Kind)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`hd.kind_${k}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label={t('hd.priority')}>
          <div className="segmented subtle hd-prio-pick" role="radiogroup" aria-label={t('hd.priority')}>
            {[0, 1, 2, 3].map((p) => (
              <button key={p} type="button" role="radio" aria-checked={f.priority === p} className={f.priority === p ? 'active' : ''} onClick={() => set('priority', p)}>
                <Priority p={p} label />
              </button>
            ))}
          </div>
        </Field>
        <div className="setting-row">
          <div>
            <strong>{t('hd.assign_me')}</strong>
            <p className="muted">{t('hd.assign_me_hint')}</p>
          </div>
          <Switch checked={f.assign_me} onChange={(v) => set('assign_me', v)} label={t('hd.assign_me')} />
        </div>
      </div>
      {picking ? (
        <CustomerPicker
          title={t('hd.pick_customer')}
          onClose={() => setPicking(false)}
          onPick={(c) => {
            setCustomer({ id: c.id, name: c.name });
            setPicking(false);
          }}
        />
      ) : null}
    </Dialog>
  );
}

// ---- teams --------------------------------------------------------------------------------------

function TeamsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { can } = useSession();
  const { data, reload } = useAction<Team[]>('helpdesk.teams');
  const [editing, setEditing] = useState<Team | 'new' | null>(null);
  const manage = can('helpdesk.manage');
  if (!data) return <Skeleton h={300} />;
  return (
    <div className="form-stack">
      <div className="hd-tab-head">
        <p className="muted">{t('hd.teams_hint')}</p>
        {manage ? (
          <Button icon="plus" onClick={() => setEditing('new')}>
            {t('hd.new_team')}
          </Button>
        ) : null}
      </div>
      {!data.length ? (
        <EmptyState icon="users" title={t('hd.no_teams')} body={t('hd.no_teams_hint')} />
      ) : (
        <div className="hd-team-grid">
          {data.map((x) => (
            <article key={x.id} className={`panel hd-team-card${x.active ? '' : ' off'}`}>
              <header>
                <span className={`hd-team-mark tone-${x.color}`} aria-hidden="true">
                  {x.name.charAt(0).toUpperCase()}
                </span>
                <div className="vat-party">
                  <strong>{x.name}</strong>
                  <span className="muted small">{x.description ?? t(`hd.as_${x.assignment}` as MessageKey)}</span>
                </div>
                {manage ? <IconButton icon="pen" label={t('hd.edit')} onClick={() => setEditing(x)} /> : null}
              </header>
              <dl className="lc-facts">
                <dt>{t('hd.assignment')}</dt>
                <dd>{t(`hd.as_${x.assignment}` as MessageKey)}</dd>
                <dt>{t('hd.members')}</dt>
                <dd>{x.member_names.length ? x.member_names.join(', ') : '—'}</dd>
                <dt>{t('hd.support_page')}</dt>
                <dd>{x.public ? t('hd.shown') : t('hd.hidden')}</dd>
              </dl>
              {!x.active ? <StatusBadge tone="neutral" icon="archive" label={t('hd.archived')} /> : null}
            </article>
          ))}
        </div>
      )}
      {editing ? (
        <TeamDialog
          team={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </div>
  );
}

function TeamDialog({ team, onClose, onSaved }: { team: Team | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: setup } = useAction<Setup>('helpdesk.setup');
  const [f, setF] = useState({
    name: team?.name ?? '',
    description: team?.description ?? '',
    color: team?.color ?? 'blue',
    assignment: team?.assignment ?? 'balanced',
    members: team?.members ?? [],
    public: team?.public ?? true,
    active: team?.active ?? true,
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF({ ...f, [k]: v });
  const errors = {
    name: f.name.trim().length < 2 ? t('hd.e_team_name') : null,
    members: f.assignment !== 'manual' && !f.members.length ? t('hd.e_members') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('helpdesk.save_team', { id: team?.id ?? null, ...f, name: f.name.trim(), description: f.description.trim() || null });
      await onSaved();
    }, t('hd.team_saved'));
  };
  const remove = () =>
    void run(async () => {
      const r = await action<{ archived: boolean }>('helpdesk.delete_team', { id: team!.id });
      await onSaved();
      return r;
    }, t('hd.team_removed'));
  return (
    <Dialog
      title={team ? t('hd.edit_team') : t('hd.new_team')}
      onClose={onClose}
      footer={
        <>
          {team ? (
            <Button variant="ghost" icon="trash" busy={busy} onClick={remove}>
              {team.tickets ? t('hd.archive') : t('hd.delete')}
            </Button>
          ) : null}
          <span className="spacer" />
          <Button onClick={onClose}>{t('hd.cancel')}</Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('hd.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('hd.team_name')} required error={tried ? errors.name : null}>
          <input className="input" value={f.name} maxLength={60} placeholder={t('hd.team_name_hint')} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label={t('hd.team_description')} hint={t('hd.team_description_hint')}>
          <input className="input" value={f.description} maxLength={300} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <Field label={t('hd.color')}>
          <div className="hd-colors" role="radiogroup" aria-label={t('hd.color')}>
            {COLORS.map((c) => (
              <button key={c} type="button" role="radio" aria-checked={f.color === c} aria-label={c} className={`hd-color tone-${c}${f.color === c ? ' on' : ''}`} onClick={() => set('color', c)}>
                {f.color === c ? <Icon name="check" size={14} /> : null}
              </button>
            ))}
          </div>
        </Field>
        <Field label={t('hd.assignment')}>
          <div className="hd-assign">
            {ASSIGNMENT.map((a) => (
              <label key={a} className={`hd-assign-opt${f.assignment === a ? ' on' : ''}`}>
                <input type="radio" name="assignment" checked={f.assignment === a} onChange={() => set('assignment', a)} />
                <strong>{t(`hd.as_${a}` as MessageKey)}</strong>
                <span className="muted small">{t(`hd.as_${a}_hint` as MessageKey)}</span>
              </label>
            ))}
          </div>
        </Field>
        <Field label={t('hd.members')} error={tried ? errors.members : null}>
          <div className="hd-members">
            {(setup?.people ?? []).map((p) => (
              <label key={p.id} className="hd-member">
                <input type="checkbox" checked={f.members.includes(p.id)} onChange={(e) => set('members', e.target.checked ? [...f.members, p.id] : f.members.filter((m) => m !== p.id))} />
                <span>{p.name}</span>
              </label>
            ))}
          </div>
        </Field>
        <div className="setting-row">
          <div>
            <strong>{t('hd.on_portal')}</strong>
            <p className="muted">{t('hd.on_portal_hint')}</p>
          </div>
          <Switch checked={f.public} onChange={(v) => set('public', v)} label={t('hd.on_portal')} />
        </div>
        {team ? (
          <div className="setting-row">
            <div>
              <strong>{t('hd.active')}</strong>
              <p className="muted">{t('hd.active_hint')}</p>
            </div>
            <Switch checked={f.active} onChange={(v) => set('active', v)} label={t('hd.active')} />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- service levels ------------------------------------------------------------------------------

function SlasTab({ teams }: { teams: Setup['teams'] }) {
  const { can } = useSession();
  const { data, reload } = useAction<Sla[]>('helpdesk.slas');
  const [editing, setEditing] = useState<Sla | 'new' | null>(null);
  const manage = can('helpdesk.manage');
  const teamName = (id: string | null) => (id ? (teams.find((x) => x.id === id)?.name ?? '—') : t('hd.every_team'));
  if (!data) return <Skeleton h={300} />;
  return (
    <div className="form-stack">
      <div className="hd-tab-head">
        <p className="muted">{t('hd.slas_hint')}</p>
        {manage ? (
          <Button icon="plus" onClick={() => setEditing('new')}>
            {t('hd.new_sla')}
          </Button>
        ) : null}
      </div>
      {!data.length ? (
        <EmptyState icon="clock" title={t('hd.no_slas')} body={t('hd.no_slas_hint')} />
      ) : (
        <Table
          head={[t('hd.sla_name'), t('hd.team'), t('hd.from_priority'), t('hd.first_answer'), t('hd.solution')]}
          left={[0, 1, 2]}
          phoneHide={[1]}
          rows={data.map((s) => ({
            key: s.id,
            onClick: manage ? () => setEditing(s) : undefined,
            cells: [
              <span key="n" className="vat-party">
                <strong>{s.name}</strong>
                {!s.active ? <span className="muted small">{t('hd.off')}</span> : null}
              </span>,
              teamName(s.team),
              <Priority key="p" p={s.priority} label />,
              t('hd.within_h', { n: s.first_hours }),
              t('hd.within_h', { n: s.resolve_hours }),
            ],
          }))}
        />
      )}
      {editing ? (
        <SlaDialog
          sla={editing === 'new' ? null : editing}
          teams={teams}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      ) : null}
    </div>
  );
}

function SlaDialog({ sla, teams, onClose, onSaved }: { sla: Sla | null; teams: Setup['teams']; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: sla?.name ?? '',
    team: sla?.team ?? '',
    priority: sla?.priority ?? 0,
    first_hours: sla?.first_hours ?? 8,
    resolve_hours: sla?.resolve_hours ?? 48,
    active: sla?.active ?? true,
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF({ ...f, [k]: v });
  const errors = {
    name: f.name.trim().length < 2 ? t('hd.e_sla_name') : null,
    resolve: f.resolve_hours < f.first_hours ? t('hd.e_resolve') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('helpdesk.save_sla', { id: sla?.id ?? null, ...f, name: f.name.trim(), team: f.team || null });
      await onSaved();
    }, t('hd.sla_saved'));
  };
  return (
    <Dialog
      title={sla ? t('hd.edit_sla') : t('hd.new_sla')}
      onClose={onClose}
      footer={
        <>
          {sla ? (
            <Button variant="ghost" icon="trash" busy={busy} onClick={() => void run(async () => (await action('helpdesk.delete_sla', { id: sla.id }), await onSaved()), t('hd.sla_removed'))}>
              {t('hd.delete')}
            </Button>
          ) : null}
          <span className="spacer" />
          <Button onClick={onClose}>{t('hd.cancel')}</Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('hd.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('hd.sla_name')} required error={tried ? errors.name : null}>
          <input className="input" value={f.name} maxLength={60} placeholder={t('hd.sla_name_hint')} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <div className="form-grid">
          <Field label={t('hd.team')}>
            <select className="input" value={f.team} onChange={(e) => set('team', e.target.value)}>
              <option value="">{t('hd.every_team')}</option>
              {teams.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('hd.from_priority')} hint={t('hd.from_priority_hint')}>
            <select className="input" value={f.priority} onChange={(e) => set('priority', Number(e.target.value))}>
              {[0, 1, 2, 3].map((p) => (
                <option key={p} value={p}>
                  {t(`hd.p_${PRIORITY_TONE[p]}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('hd.first_answer_within')} hint={t('hd.hours_hint')}>
            <input className="input tabular" type="number" min={1} max={720} value={f.first_hours} onChange={(e) => set('first_hours', Math.max(1, Math.round(Number(e.target.value) || 1)))} />
          </Field>
          <Field label={t('hd.solution_within')} hint={t('hd.hours_hint')} error={tried ? errors.resolve : null}>
            <input className="input tabular" type="number" min={1} max={2160} value={f.resolve_hours} onChange={(e) => set('resolve_hours', Math.max(1, Math.round(Number(e.target.value) || 1)))} />
          </Field>
        </div>
        <div className="setting-row">
          <div>
            <strong>{t('hd.active')}</strong>
            <p className="muted">{t('hd.sla_active_hint')}</p>
          </div>
          <Switch checked={f.active} onChange={(v) => set('active', v)} label={t('hd.active')} />
        </div>
      </div>
    </Dialog>
  );
}

// ---- ready-made answers --------------------------------------------------------------------------

function MacrosTab({ teams, onChange }: { teams: Setup['teams']; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const { data, reload } = useAction<Macro[]>('helpdesk.macros');
  const [editing, setEditing] = useState<Macro | 'new' | null>(null);
  const manage = can('helpdesk.manage');
  if (!data) return <Skeleton h={300} />;
  return (
    <div className="form-stack">
      <div className="hd-tab-head">
        <p className="muted">{t('hd.macros_hint')}</p>
        {manage ? (
          <Button icon="plus" onClick={() => setEditing('new')}>
            {t('hd.new_macro')}
          </Button>
        ) : null}
      </div>
      {!data.length ? (
        <EmptyState icon="note" title={t('hd.no_macros')} body={t('hd.no_macros_hint')} />
      ) : (
        <ul className="hd-macros">
          {data.map((m) => (
            <li key={m.id} className={`panel${m.active ? '' : ' off'}`}>
              <div className="hd-macro-head">
                <strong>{m.name}</strong>
                <span className="muted small">{m.team ? (teams.find((x) => x.id === m.team)?.name ?? '') : t('hd.every_team')}</span>
                {manage ? <IconButton icon="pen" label={t('hd.edit')} onClick={() => setEditing(m)} /> : null}
              </div>
              <p className="muted hd-macro-body">{m.body}</p>
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <MacroDialog
          macro={editing === 'new' ? null : editing}
          teams={teams}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </div>
  );
}

function MacroDialog({ macro, teams, onClose, onSaved }: { macro: Macro | null; teams: Setup['teams']; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({ name: macro?.name ?? '', body: macro?.body ?? '', team: macro?.team ?? '', active: macro?.active ?? true });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF({ ...f, [k]: v });
  const errors = { name: f.name.trim().length < 2 ? t('hd.e_macro_name') : null, body: !f.body.trim() ? t('hd.e_macro_body') : null };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('helpdesk.save_macro', { id: macro?.id ?? null, name: f.name.trim(), body: f.body.trim(), team: f.team || null, active: f.active });
      await onSaved();
    }, t('hd.macro_saved'));
  };
  return (
    <Dialog
      title={macro ? t('hd.edit_macro') : t('hd.new_macro')}
      onClose={onClose}
      footer={
        <>
          {macro ? (
            <Button variant="ghost" icon="trash" busy={busy} onClick={() => void run(async () => (await action('helpdesk.delete_macro', { id: macro.id }), await onSaved()), t('hd.macro_removed'))}>
              {t('hd.delete')}
            </Button>
          ) : null}
          <span className="spacer" />
          <Button onClick={onClose}>{t('hd.cancel')}</Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('hd.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="form-grid">
          <Field label={t('hd.macro_name')} required error={tried ? errors.name : null}>
            <input className="input" value={f.name} maxLength={80} placeholder={t('hd.macro_name_hint')} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field label={t('hd.team')}>
            <select className="input" value={f.team} onChange={(e) => set('team', e.target.value)}>
              <option value="">{t('hd.every_team')}</option>
              {teams.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label={t('hd.macro_body')} required error={tried ? errors.body : null} counter={`${f.body.length}/10000`}>
          <textarea className="input" rows={6} value={f.body} maxLength={10000} onChange={(e) => set('body', e.target.value)} />
        </Field>
        {macro ? (
          <div className="setting-row">
            <div>
              <strong>{t('hd.active')}</strong>
              <p className="muted">{t('hd.macro_active_hint')}</p>
            </div>
            <Switch checked={f.active} onChange={(v) => set('active', v)} label={t('hd.active')} />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- settings ----------------------------------------------------------------------------------

function SettingsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { toast, fail } = useUi();
  const { data, reload } = useAction<Settings>('helpdesk.settings');
  const [f, setF] = useState<Settings | null>(null);
  const [tried, setTried] = useState(false);
  const loaded = useRef(false);
  useEffect(() => {
    if (!data || loaded.current) return;
    loaded.current = true;
    setF({ ...data, site_url: data.site_url ?? (window.location.protocol === 'https:' ? window.location.origin : null) });
  }, [data]);
  if (!f || !data) return <Skeleton h={400} />;
  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setF({ ...f, [k]: v });
  const siteError = f.site_url && !/^https?:\/\/[a-z0-9.-]+(:\d+)?(\/[\w./-]*)?$/i.test(f.site_url) ? t('hd.e_site') : null;
  const save = () => {
    setTried(true);
    if (siteError) return;
    void run(async () => {
      const out = await action<Settings>('helpdesk.save_settings', {
        portal: f.portal,
        portal_title: f.portal_title?.trim() || null,
        portal_intro: f.portal_intro?.trim() || null,
        notify: f.notify,
        site_url: f.site_url || null,
        rating: f.rating,
        reopen_days: f.reopen_days,
        close_after_days: f.close_after_days,
      });
      setF(out);
      await reload();
      await onChange();
    }, t('hd.settings_saved'));
  };
  const copy = (s: string) => void navigator.clipboard?.writeText(s).then(() => toast(t('hd.copied')), fail);
  return (
    <div className="form-stack">
      <div className={`sms-ready-card${data.portal ? ' ok' : ''}`}>
        <Icon name={data.portal ? 'checkCircle' : 'circle'} size={18} />
        <span className="vat-party">
          <strong>{t('hd.support_page')}</strong>
          <span className="muted small">{data.portal ? t('hd.portal_on') : t('hd.portal_off')}</span>
        </span>
      </div>
      <section className="panel form-stack sub-form">
        <div className="setting-row first">
          <div>
            <h2 className="rep-sub">{t('hd.support_page')}</h2>
            <p className="muted">{t('hd.portal_hint')}</p>
          </div>
          <Switch checked={f.portal} onChange={(v) => set('portal', v)} label={t('hd.support_page')} />
        </div>
        <Field label={t('hd.portal_title')} hint={t('hd.portal_title_hint')}>
          <input className="input" value={f.portal_title ?? ''} maxLength={80} placeholder={t('sup.title')} onChange={(e) => set('portal_title', e.target.value || null)} />
        </Field>
        <Field label={t('hd.portal_intro')} counter={`${(f.portal_intro ?? '').length}/600`}>
          <textarea className="input" rows={3} value={f.portal_intro ?? ''} maxLength={600} placeholder={t('sup.intro')} onChange={(e) => set('portal_intro', e.target.value || null)} />
        </Field>
        <Field label={t('hd.site_url')} hint={t('hd.site_url_hint')} error={tried ? siteError : null}>
          <input className="input" value={f.site_url ?? ''} spellCheck={false} placeholder="https://" onChange={(e) => set('site_url', e.target.value.trim().replace(/\/+$/, '') || null)} />
        </Field>
        {data.portal_url ? (
          <div className="lc-link-box">
            <p className="muted small">{t('hd.portal_link_hint')}</p>
            <div className="lc-link">
              <code>{data.portal_url}</code>
              <IconButton icon="copy" label={t('hd.copy_link')} onClick={() => copy(data.portal_url!)} />
              <a className="btn btn-ghost btn-sm" href={data.portal_path} target="_blank" rel="noreferrer">
                <Icon name="globe" size={16} />
                <span>{t('hd.open')}</span>
              </a>
            </div>
          </div>
        ) : null}
      </section>
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('hd.customers_section')}</h2>
          <p className="muted small">{t('hd.customers_section_hint')}</p>
        </div>
        <div className="setting-row">
          <div>
            <strong>{t('hd.notify')}</strong>
            <p className="muted">{data.mail_ready ? t('hd.notify_hint') : t('hd.notify_no_mail')}</p>
          </div>
          <Switch checked={f.notify} onChange={(v) => set('notify', v)} label={t('hd.notify')} />
        </div>
        <div className="setting-row">
          <div>
            <strong>{t('hd.rating')}</strong>
            <p className="muted">{t('hd.rating_hint')}</p>
          </div>
          <Switch checked={f.rating} onChange={(v) => set('rating', v)} label={t('hd.rating')} />
        </div>
        <div className="form-grid">
          <Field label={t('hd.close_after')} hint={t('hd.close_after_hint')}>
            <input
              className="input tabular"
              type="number"
              min={1}
              max={90}
              value={f.close_after_days}
              onChange={(e) => set('close_after_days', Math.min(90, Math.max(1, Math.round(Number(e.target.value) || 1))))}
            />
          </Field>
          <Field label={t('hd.reopen_days')} hint={t('hd.reopen_days_hint')}>
            <input
              className="input tabular"
              type="number"
              min={0}
              max={365}
              value={f.reopen_days}
              onChange={(e) => set('reopen_days', Math.min(365, Math.max(0, Math.round(Number(e.target.value) || 0))))}
            />
          </Field>
        </div>
      </section>
      <div className="row-actions">
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {t('hd.save_settings')}
        </Button>
      </div>
    </div>
  );
}
